import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import TextField from "../../components/carbon/Form/TextField";
import TextareaField from "../../components/carbon/Form/TextareaField";
import ColorField from "../../components/carbon/Form/ColorField";
import { useCreateTag, useUpdateTag } from "../../hooks/useTags";
import { useNotification } from "../../context";
import { TagResponse } from "../../types";
import {
  tagFormValidationSchema,
  TagFormValues,
} from "./tagFormValidationSchema";

// The exact ten-color palette `TagsManagement.tsx` hard-coded (its own "Color presets" block,
// ~line 346) — preserved verbatim so `ColorField`'s swatch buttons (and their `aria-label`s,
// which `e2e/tags.spec.ts` drives directly) render identically to the pre-Carbon screen.
const COLOR_PRESETS = [
  "#EF4444", // red
  "#F97316", // orange
  "#F59E0B", // amber
  "#10B981", // emerald
  "#3B82F6", // blue
  "#6366F1", // indigo
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#64748B", // slate
  "#06B6D4", // cyan
];
const DEFAULT_COLOR = "#3B82F6";

type TagFormProps = {
  tag: TagResponse | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  // Optional hook for callers that need the freshly created tag back (e.g. the material-form
  // `TagPicker`, which auto-selects it). Fires only on a successful *create*; the Tags-page
  // caller omits it and is unaffected.
  onCreated?: (tag: TagResponse) => void;
};

/**
 * The create/edit tag dialog, on Carbon's `ComposedModal` — same precedent `UserGroupForm.tsx`
 * set for this codebase's first RHF+Yup modal form: a `Controller`-bound field per input
 * (`TextField`/`TextareaField`/`ColorField`), `tagFormValidationSchema` extracted to its own
 * module, and `ModalFooter`'s own `loadingStatus`/`primaryButtonDisabled` covering the in-flight
 * mutation instead of a bespoke spinner.
 *
 * No second `ConfirmDialog` step before saving: the pre-Carbon form never had one either (only
 * *deleting* a tag does — see `Tags.tsx`), so this preserves that rather than adding a
 * confirmation nothing asked for, same reasoning `UserGroupForm.tsx` documents for itself.
 *
 * `description` carries no `enableCounter`/`maxCount` — read the pre-Carbon screen's own
 * `<textarea>` (TagsManagement.tsx line ~878) and its `CreateTagRequest`/`UpdateTagRequest`
 * backend payloads before assuming one: neither the FE nor the backend (`Tag.description` is a
 * plain `TEXT` column, no `@Size`) enforces a length limit today, so `TextareaField` is used
 * exactly the way `TextField` is — no counter invented that the old screen never had.
 *
 * Error handling prefers the server's own message (e.g. a duplicate-name conflict) over the
 * generic fallback, the same convention `UserGroupForm.tsx` uses — a deliberate improvement over
 * the pre-Carbon screen, which always showed a generic "Error creating/updating tag" regardless
 * of what the backend actually said.
 */
const TagForm = ({ tag, open, setOpen, onCreated }: TagFormProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const close = () => setOpen(false);

  const getErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message || fallback;

  const { mutate: createTag, isPending: isCreating } = useCreateTag(
    (createdTag) => {
      openNotification(t("tags.tagCreated"));
      onCreated?.(createdTag);
      close();
    },
    (error) => openNotification(getErrorMessage(error, t("tags.createError")))
  );

  const { mutate: updateTag, isPending: isUpdating } = useUpdateTag(
    () => {
      openNotification(t("tags.tagUpdated"));
      close();
    },
    (error) => openNotification(getErrorMessage(error, t("tags.updateError")))
  );

  const isSaving = isCreating || isUpdating;

  const defaultValues: TagFormValues = useMemo(
    () => ({
      name: tag?.name ?? "",
      description: tag?.description ?? "",
      color: tag?.color ?? DEFAULT_COLOR,
    }),
    [tag]
  );

  const { control, handleSubmit, reset } = useForm<TagFormValues>({
    resolver: yupResolver(tagFormValidationSchema),
    defaultValues,
  });

  // Re-seed the form whenever it opens for a different tag (or opens fresh for a new one) — same
  // reasoning and the same [tag, open] dependency `UserGroupForm.tsx`'s own reset effect uses.
  useEffect(() => {
    reset(defaultValues);
  }, [tag, open, reset, defaultValues]);

  const onValid = (values: TagFormValues) => {
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      color: values.color,
    };
    if (tag) {
      updateTag({ id: tag.id, data: payload });
    } else {
      createTag(payload);
    }
  };

  const title = tag ? t("tags.editTag") : t("tags.createNewTag");

  return (
    <ComposedModal open={open} aria-label={title} onClose={close}>
      <ModalHeader title={title} />
      <ModalBody hasForm>
        <TextField
          name="name"
          label={t("tags.tagName")}
          placeholder={t("tags.tagNamePlaceholder")}
          control={control}
          required
          disabled={isSaving}
        />
        <TextareaField
          name="description"
          label={t("tags.description")}
          placeholder={t("tags.descriptionPlaceholder")}
          control={control}
          optional
          rows={3}
          disabled={isSaving}
        />
        <ColorField
          name="color"
          label={t("tags.color")}
          control={control}
          presets={COLOR_PRESETS}
          customColorLabel={t("tags.customColor")}
          disabled={isSaving}
        />
      </ModalBody>
      <ModalFooter
        secondaryButtonText={t("cta.cancel")}
        primaryButtonText={
          isSaving ? t("tags.saving") : tag ? t("cta.save") : t("cta.create")
        }
        primaryButtonDisabled={isSaving}
        loadingStatus={isSaving ? "active" : "inactive"}
        loadingDescription={t("cta.loading")}
        onRequestSubmit={handleSubmit(onValid)}
      >
        {null}
      </ModalFooter>
    </ComposedModal>
  );
};

export default TagForm;
